/**
 * Unified tracking utility for Facebook Pixel + Google Ads + GA4.
 *
 * Usage anywhere in the app:
 *   import { trackEvent } from "../utils/tracking";
 *   trackEvent.leadSubmit({ service: "Screen Repair" });
 *   trackEvent.phoneCall();
 *   trackEvent.bookingStart();
 *
 * All tracking is SAFE if pixel IDs aren't set — it silently no-ops.
 */

const FB_PIXEL_ID = process.env.REACT_APP_FACEBOOK_PIXEL_ID;
const GOOGLE_ADS_ID = process.env.REACT_APP_GOOGLE_ADS_ID;
const CONV_LEAD = process.env.REACT_APP_GOOGLE_ADS_CONVERSION_LABEL_LEAD;
const CONV_CALL = process.env.REACT_APP_GOOGLE_ADS_CONVERSION_LABEL_CALL;

/** Install Facebook Pixel once on page load (called from App/index). */
export const initFacebookPixel = () => {
  if (!FB_PIXEL_ID || typeof window === "undefined") return;
  if (window.fbq) return; // already initialized

  /* eslint-disable */
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod
        ? n.callMethod.apply(n, arguments)
        : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = "2.0";
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(
    window,
    document,
    "script",
    "https://connect.facebook.net/en_US/fbevents.js"
  );
  /* eslint-enable */

  window.fbq("init", FB_PIXEL_ID);
  window.fbq("track", "PageView");
};

/** Install Google Ads tracking once. (GA4 gtag already loaded in index.html.) */
export const initGoogleAds = () => {
  if (!GOOGLE_ADS_ID || typeof window === "undefined") return;
  if (window._googleAdsInited) return;
  window._googleAdsInited = true;

  // Load Google Ads tag (reuses the dataLayer that GA4 already set up)
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}`;
  document.head.appendChild(s);

  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = window.gtag || gtag;
  window.gtag("js", new Date());
  window.gtag("config", GOOGLE_ADS_ID);
};

/** Safely fire Facebook Pixel event. No-op if pixel isn't loaded. */
const fbq = (event, params = {}) => {
  if (typeof window === "undefined" || !window.fbq) return;
  window.fbq("track", event, params);
};

/** Safely fire Google Ads conversion. No-op if tag isn't loaded. */
const gtagConversion = (conversionLabel, value = 0) => {
  if (typeof window === "undefined" || !window.gtag) return;
  if (!GOOGLE_ADS_ID || !conversionLabel) return;
  window.gtag("event", "conversion", {
    send_to: `${GOOGLE_ADS_ID}/${conversionLabel}`,
    value,
    currency: "USD",
  });
};

/** Safely fire GA4 event (gtag loaded in index.html). */
const ga4Event = (name, params = {}) => {
  if (typeof window === "undefined" || !window.gtag) return;
  window.gtag("event", name, params);
};

// ============================================================================
// Exposed named events — call these from components.
// ============================================================================
export const trackEvent = {
  /** Fired when someone submits the contact / quote form. */
  leadSubmit: (params = {}) => {
    fbq("Lead", params);
    ga4Event("generate_lead", params);
    gtagConversion(CONV_LEAD, 50); // assume $50 average lead value
  },

  /** Fired when someone taps the phone number link. */
  phoneCall: (params = {}) => {
    fbq("Contact", { method: "phone", ...params });
    ga4Event("click_phone", params);
    gtagConversion(CONV_CALL, 75);
  },

  /** Fired when someone opens the booking widget / clicks Book Repair. */
  bookingStart: (params = {}) => {
    fbq("InitiateCheckout", params);
    ga4Event("begin_booking", params);
  },

  /** Fired when someone confirms a booking slot. */
  bookingComplete: (params = {}) => {
    fbq("Schedule", params);
    ga4Event("booking_complete", params);
    gtagConversion(CONV_LEAD, 100);
  },

  /** Fired when someone taps the WhatsApp button. */
  whatsappClick: (params = {}) => {
    fbq("Contact", { method: "whatsapp", ...params });
    ga4Event("click_whatsapp", params);
  },

  /** Fired when someone taps the SMS button. */
  smsClick: (params = {}) => {
    fbq("Contact", { method: "sms", ...params });
    ga4Event("click_sms", params);
  },

  /** Generic event — useful for ad-hoc tracking. */
  custom: (name, params = {}) => {
    ga4Event(name, params);
    fbq(name, params);
  },
};

export default trackEvent;
